import { format } from 'date-fns';
import type {
  SourceStatus,
  SourceForActions,
  ExtendedActionCardPayload,
} from './types';
import {
  SOURCE_STATUS,
  PROCESSING_STATUSES,
  DATE_FORMAT,
  ACTION_TYPES,
  LABELS,
  FILE_TYPES,
} from './constants';
import { notify } from '../../../../app';

export const formatDate = (dateString: string): string => {
  return format(new Date(dateString), DATE_FORMAT);
};

export const getSourceStatus = (
  status: string | null | undefined = '',
  isActive?: boolean
): SourceStatus => {
  if (isActive) return SOURCE_STATUS.ACTIVE;

  return PROCESSING_STATUSES.includes((status || '') as any)
    ? SOURCE_STATUS.IN_PROGRESS
    : SOURCE_STATUS.INACTIVE;
};

export const showActivateButton = (
  isActive: boolean,
  status: string | null | undefined
): boolean => {
  return !isActive && getSourceStatus(status) !== SOURCE_STATUS.IN_PROGRESS;
};

export const createSourceActions = (
  source: SourceForActions,
  handlers: {
    onRename: (id: string) => void;
    onToggleActive: (id: string) => void;
    onViewDetails: (id: string, name: string) => void;
    onDelete: (id: string) => void;
    onDownload: (id: string, name: string) => void;
  }
): Array<{
  label: string;
  icon: React.ReactNode;
  labelClassName?: string;
  onClick: () => void;
}> => {
  const { active, status, id, name } = source;

  if (status === 'failed') {
    return [
      {
        label: LABELS.DELETE,
        icon: null,
        labelClassName: 'text-red-400',
        onClick: () => handlers.onDelete(id),
      },
    ];
  }

  const actions = [
    {
      label: LABELS.RENAME,
      icon: null,
      onClick: () => handlers.onRename(id),
    },
    ...(active
      ? [
          {
            label: LABELS.DEACTIVATE,
            icon: null,
            onClick: () => handlers.onToggleActive(id),
          },
        ]
      : []),
    {
      label: LABELS.CHUNK_VIEWER,
      icon: null,
      onClick: () => handlers.onViewDetails(id, name),
    },
    {
      label: LABELS.DOWNLOAD,
      icon: null,
      onClick: () => handlers.onDownload(id, name),
    },
    {
      label: LABELS.DELETE,
      icon: null,
      onClick: () => handlers.onDelete(id),
    },
  ];

  return actions.filter((action): action is NonNullable<typeof action> =>
    Boolean(action)
  );
};

export const handleSourceActivation = (
  id: string,
  vectorized: boolean = false,
  fileType: string | undefined,
  handlers: {
    onToggleActive: (id: string, active?: boolean) => void;
    onActivateKnowledgeSource: (payload: { dataSourceId: string }) => void;
    onOpenActivateModal: () => void;
    onSetActionPayload: (payload: ExtendedActionCardPayload) => void;
  }
): void => {
  if (vectorized) {
    handlers.onToggleActive(id, true);
  } else if (fileType === FILE_TYPES.JSON) {
    handlers.onActivateKnowledgeSource({
      dataSourceId: id,
    });
  } else {
    handlers.onOpenActivateModal();
    handlers.onSetActionPayload({ action: ACTION_TYPES.ACTIVATE, id });
  }
};

export const handleDownloadFile = async (
  downloadFunction: () => Promise<Blob>,
  fileName: string,
  setDownloading: (downloading: boolean) => void,
  successMessage: string = 'File downloaded successfully',
  errorMessage: string = 'Failed to download file'
): Promise<void> => {
  setDownloading(true);
  try {
    const blob = await downloadFunction();

    if (!(blob instanceof Blob)) {
      notify.error('Invalid response received.');
      return;
    }

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName || 'file'}.txt`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);

    notify.success(successMessage);
  } catch (error) {
    notify.error(errorMessage);
  } finally {
    setDownloading(false);
  }
};
